import React, { useEffect, useRef, useState, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, RadioChangeEvent, Typography } from "antd";
import { MessageOutlined } from "@mui/icons-material";
import SessionManager from "@Core/session";
import NotificationService from "@Services/NotificationService";
import { NotificationsList } from "@Components/notifications/NotificationsList";
import { QueryKeys, ThreeMinutes } from "@Constants";
import { INotificationModel } from "@Models/INotificationPayload";
import "@Styles/components/notifications.scss";
import { MainContext } from "@Context/provider";
import { ViewMode } from "@Context/state";

export type NotificationItemAction = (
    method: "DELETE" | "PATCH" | "POST" | "PUT",
    url: string,
    notificationId: string,
    category?: string
) => void;
export type NotificationsFilter = "all" | "unread";

const Notifications: React.FC = () => {
    const { Text } = Typography;
    const { ui: uiState } = useContext(MainContext);
    const [filteredNotifications, setFilteredNotifications] = useState<INotificationModel[]>();
    const [filter, setFilter] = useState<NotificationsFilter>("all");
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const queryClient = useQueryClient();

    /**
     Listen for an OutsideClick event (outside the Notifications component)
     **/
    const ref = useRef(null);
    const onClickOutside = () => {
        setIsOpen(false);
    };
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                onClickOutside();
            }
        };
        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, []);

    /**
     * These callbacks run after a notification actions has completed
     */
    const categoryCallbacks = {
        connection: async () => queryClient.invalidateQueries([QueryKeys.UserConnections]),
    };

    /**
     * Handle notification actions
     * @param method
     * @param url
     * @param id
     **/
    const handleNotificationActionClick: NotificationItemAction = async (method, url, id, category = "") => {
        const service = new NotificationService();
        await service.commonNotificationAction(method, url);
        await service.setNotificationViewed(id);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
        categoryCallbacks[category]?.();
    };

    const { isFetching, data } = useQuery(
        [QueryKeys.Notifications],
        async () => {
            const service = new NotificationService();
            const result = await service.getNotifications(SessionManager.getServiceUser()?.userId);
            if (!result.hasErrors) return result.value;
            return [];
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
            select: (data) => {
                return data.sort(function (a, b) {
                    const curr = new Date(a.dateCreated).getTime();
                    const next = new Date(b.dateCreated).getTime();
                    return next - curr;
                });
            },
        }
    );

    useEffect(() => {
        const result = data?.slice().filter((item) => (filter === "all" ? true : item.status.toLowerCase() === "new"));
        setFilteredNotifications(result);
    }, [data, filter]);

    const onNotificationsClick = () => {
        setIsOpen(!isOpen);
    };

    /**
     * Handle SetViewed notification action
     * @param notificationId
     **/
    const onSetViewedClick = async (notificationId: string) => {
        const service = new NotificationService();
        await service.setNotificationViewed(notificationId);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Handle SetUnread notification action
     * @param notificationId
     **/
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onSetUnreadClick = async (notificationId: string) => {
        const service = new NotificationService();
        await service.setNotificationUnread(notificationId);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Handle all notifications as viewed
     **/
    const setOnSetAllViewedClick = async () => {
        const filterNewNotifications = data?.slice().filter((item) => item.status.toLowerCase() === "new");
        const service = new NotificationService();
        await Promise.all([
            ...filterNewNotifications.map((notification) => {
                return service.setNotificationViewed(notification.notificationId);
            }),
        ]);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Set notifications filter: 'all' or 'unread'
     **/
    const onFilterClick = (e: RadioChangeEvent) => {
        setFilter(e.target.value);
    };

    const amountOfNewNotifications = () => {
        let res = 0;
        if (data) {
            res = data.filter((item) => item.status.toLowerCase() === "new")?.length;
        }
        return res;
    };

    return (
        <div className="notifications" ref={ref}>
            <Button
                type="text"
                onClick={onNotificationsClick}
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
                <MessageOutlined style={{ color: "white" }} />
                <Text style={{ color: "white", fontSize: "15px" }}>
                    {uiState.mode === ViewMode.desktop ? <span>Notifications</span> : null}
                    <span>{` (${amountOfNewNotifications()})`}</span>
                </Text>
            </Button>
            {isOpen ? (
                <NotificationsList
                    isFetching={isFetching}
                    onActionClick={handleNotificationActionClick}
                    data={filteredNotifications}
                    onSetViewedClick={onSetViewedClick}
                    onFilterClick={onFilterClick}
                    onSetAllViewedClick={setOnSetAllViewedClick}
                    filter={filter}
                />
            ) : (
                <div />
            )}
        </div>
    );
};
export default Notifications;
